import { ActionIcon, Box, Group, Menu, Stack, Title, useMantineColorScheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import ThemeSwitcher from "../ThemeHandler/ThemeSwitcher";

import { UserAvatar } from "@/components/elements";
import BetaBadge from "@/components/elements/Banner/BetaBadge";
import { IconLogout } from "@/components/icons";

import type { User } from "@/types/user";
import type { FC, ReactNode } from "react";
import type { ThemeSelectorProps } from "../ThemeHandler";

export type LogoutProps = {
  label: string;
  onClick: () => void;
};

export type ThemeProps = Pick<ThemeSelectorProps, "label" | "placeholder" | "schemas">;

export type UserMenuItem = {
  label: string;
  leftSection?: ReactNode;
  to: string;
};

export type UserMenuProps = {
  items?: UserMenuItem[];
  language?: boolean; // TODO
  logout?: LogoutProps;
  theme?: boolean | ThemeProps;
  user?: User;
};

export const UserMenu: FC<UserMenuProps> = ({ items = [], logout, user }) => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure();
  const { colorScheme } = useMantineColorScheme();

  const isDark = colorScheme === "dark";

  const userText = user?.name ?? user?.email;

  return (
    <Menu opened={opened} onOpen={open} onClose={close} position="bottom-end" shadow="md">
      <Menu.Target>
        <Box component="span" display="inline-block">
          <ActionIcon size="xl" radius="xl" variant="subtle" aria-label={t("common.open_menu")}>
            <UserAvatar variant="white" user={user} enableUpload={false} />
          </ActionIcon>
        </Box>
      </Menu.Target>
      <Menu.Dropdown px={0}>
        <Stack gap="md" px="md" py="md">
          <Stack gap="sm">
            <Box px="md">
              <BetaBadge />
            </Box>

            <Group justify="space-between" px="md">
              <Title order={3}>{userText}</Title>
            </Group>
          </Stack>

          <Box px="md">
            <ThemeSwitcher layout="complex" />
          </Box>

          <Menu.Divider />
          {items.map(({ label, leftSection, to }, index) => (
            <Menu.Item
              key={index}
              c={isDark ? "primary.2" : "primary"}
              component={NavLink}
              leftSection={leftSection}
              to={to}
            >
              {label}
            </Menu.Item>
          ))}
          {logout && (
            <Menu.Item c="red" leftSection={<IconLogout />} onClick={logout.onClick}>
              {logout.label}
            </Menu.Item>
          )}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};
